<template>
	<div class="relative h-full w-full">
		<div ref="mapElement" class="h-full w-full" />

		<!-- Stockland Custom Buttons -->
		<div v-if="isStocklandTheme" @click="toggleFullscreen">
			<stockland-button
				type="button"
				variant="style-1"
				size="small"
				icon="expand"
				layout="icon-only"
				additional-button-classes="expand-map-button absolute top-4 right-4 md:top-6 md:right-6 bg-white dark-theme:bg-primary-background hover:!bg-primary-accent active:!bg-primary-accent !w-9 !h-9 md:!w-11 md:!h-11 !min-h-[36px] md:!min-h-[44px] group/button"
				additional-icon-classes="!fill-primary-accent group-hover/button:!fill-primary-background"
				is-visible
			/>
		</div>

		<div
			v-if="isStocklandTheme"
			class="gmaps-zoom-buttons absolute bottom-4 md:bottom-6 right-4 md:right-6 flex flex-col gap-3 md:gap-1"
		>
			<stockland-button
				type="button"
				variant="style-1"
				size="small"
				icon="plus"
				layout="icon-only"
				additional-button-classes="zoom-control-in bg-primary-background hover:!bg-primary-accent active:!bg-primary-accent w-9 md:w-11 h-9 md:h-11 group/button"
				additional-icon-classes="!fill-primary-accent group-hover/button:!fill-primary-background group-active/button:!fill-primary-background"
				is-visible
			/>
			<stockland-button
				type="button"
				variant="style-1"
				size="small"
				icon="minus"
				layout="icon-only"
				additional-button-classes="zoom-control-out bg-primary-background hover:!bg-primary-accent active:!bg-primary-accent w-9 md:w-11 h-9 md:h-11 group/button"
				additional-icon-classes="!fill-primary-accent group-hover/button:!fill-primary-background group-active/button:!fill-primary-background"
				is-visible
			/>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, Ref, ref, SetupContext, watch } from 'vue';

import { GmapsMapController } from './gmaps-map-controller';
import { Directions, LatLng, MarkerData, Properties } from './interfaces';
import * as MapStyles from './maps-styles';

function useGmapsMap(
	props: Properties,
	context: SetupContext,
	mapElement: Ref<HTMLElement | null | undefined>,
	map: Ref<GmapsMapController | null | undefined>,
	onMapError: (error: google.maps.DirectionsResult) => void
) {
	onMounted(async () => {
		// Check if Momenta theme
		const appElement = document.querySelector('#app');
		const momentaTheme = appElement && appElement.dataset.theme === 'momenta';

		const mapOptions = {
			center: props.origin,
			styles: momentaTheme ? MapStyles.Momenta : MapStyles.Stockland,
			mapTypeControl: false,
			disableDefaultUI: props.isStocklandTheme,
		};
		if (props.zoom) {
			mapOptions.zoom = props.zoom;
		}
		if (props.isStocklandTheme) {
			mapOptions.disableDefaultUI = props.isStocklandTheme;
		}
		map.value = await GmapsMapController.mountMap(mapElement.value as HTMLElement, mapOptions);
		context.emit('onMapMount', map.value);

		map.value.setMarkersFromMarkerData(props.markers, !props.zoom, false);

		if (props.route) {
			map.value
				?.route(props.route.origin, props.route.destination, props.route.markerImage)
				.catch((error) => onMapError(error));
		}

		if (!props.cluster) {
			map.value.clusteringEnabled = false;
		}

		if (mapOptions.disableDefaultUI) {
			const zoomInControls = document.querySelectorAll<HTMLButtonElement>('.zoom-control-in');
			zoomInControls.forEach((control) => {
				control.addEventListener('click', function () {
					map.value?.setMapZoom(map.value.map.zoom + 1);
				});
			});
			const zoomOutControls = document.querySelectorAll<HTMLButtonElement>('.zoom-control-out');
			zoomOutControls.forEach((control) => {
				control.addEventListener('click', function () {
					map.value?.setMapZoom(map.value.map.zoom - 1);
				});
			});

			map.value.setMarkersFromMarkerData(props.markers, !props.zoom, false);
		}
	});

	watch(
		() => props.origin,
		(origin: LatLng) => {
			if (origin) {
				map.value?.setMapCenter(origin);
			}
		}
	);

	watch(
		() => props.zoom,
		(zoom: number) => {
			if (zoom) {
				map.value?.setMapZoom(zoom);
			} else {
				map.value?.setMapZoom(12);
			}
		}
	);

	watch(
		() => props.markers,
		(markers: MarkerData[]) => {
			map.value?.setMarkersFromMarkerData(markers);
		}
	);

	watch(
		() => props.route,
		(route: Directions | undefined) => {
			if (route) {
				map.value
					?.route(route.origin, route.destination, route.markerImage)
					.catch((error) => onMapError(error));
			} else {
				map.value?.clearRoute();
			}
		}
	);
	/**
	 * Toggles fullscreen of map instance relative to the clicked button
	 * @param {Event} event - The event object from the click event.
	 */
	function toggleFullscreen(event: Event) {
		const clickedElement = event.currentTarget as HTMLElement;
		if (!document.fullscreenElement && clickedElement && clickedElement.parentElement) {
			const menuOverlayMapContainer = clickedElement.parentElement;
			menuOverlayMapContainer.requestFullscreen().catch((err) => {
				console.error(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
			});
		} else {
			document.exitFullscreen();
		}
	}

	return {
		toggleFullscreen,
		onMounted,
	};
}

export default defineComponent({
	name: 'gmaps-map',
	props: {
		cluster: {
			type: Boolean as PropType<boolean>,
			default: true,
		},
		markers: {
			type: Array as PropType<MarkerData[]>,
			default: () => [],
		},
		origin: {
			type: Object as PropType<LatLng | undefined>,
			default: () => {
				return { lat: -34.397, lng: 150.644 };
			},
		},
		route: {
			type: Object as PropType<Directions | undefined>,
			default: undefined,
		},
		zoom: {
			type: [String, Number, undefined] as PropType<number | undefined>,
			default: undefined,
		},
		isStocklandTheme: {
			type: Boolean as PropType<boolean>,
			default: false,
		},
	},
	emits: ['onMapError', 'onMapMount'],
	setup(props: Properties, context: SetupContext) {
		const mapElement = ref<HTMLElement | null>();
		const map = ref<GmapsMapController | null>();
		const onMapError = (error: google.maps.DirectionsResult) => context.emit('onMapError', error);

		const { ...methods } = useGmapsMap(props, context, mapElement, map, onMapError);

		return {
			// ...toRefs(props),
			mapElement,
			map,
			...methods,
		};
	},
});
</script>
<style lang="postcss"></style>
